import { type FocusEvent, useRef } from 'react'

import { useFocusRing } from '@react-aria/focus'
import { useHover } from '@react-aria/interactions'
import { IconSearch } from '@tabler/icons-react'
import type { FieldValues, Path } from 'react-hook-form'
import { get, useFormContext } from 'react-hook-form'

import { useMergedRefs, useUnique } from '@subaio/components'
import type { CommonInputProps } from '@subaio/components/src/HookForm'

import { ColumnLabel, FieldWrapper, InputComponent } from '../Shared'
import { IconWrapper } from './IconWrapper'
import { InputContainer } from './InputContainer'

export const Search = <TFieldValues extends FieldValues, TPath extends Path<TFieldValues>>({
	isRequired = false,
	isDisabled = false,
	name,
	autoComplete = 'off',
	isReadOnly = false,
	placeholder,
	shouldAutofocus,
}: CommonInputProps<TPath>) => {
	const id = useUnique(name)
	const {
		register,
		formState: { errors },
	} = useFormContext<TFieldValues>()
	const { isFocusVisible, focusProps, isFocused } = useFocusRing({
		isTextInput: true,
		autoFocus: shouldAutofocus,
	})
	const { hoverProps, isHovered } = useHover({})
	const inputRef = useRef<HTMLInputElement | HTMLTextAreaElement>(null)

	const inputProps = register(name, {
		required: isRequired ? 'This field is required' : false,
		disabled: isDisabled,
	})

	const error = get(errors, name)
	const hasError = Boolean(error)

	const defaultProps = {
		...focusProps,
		...inputProps,
		autoComplete,
		autoFocus: shouldAutofocus,
		hasError,
		id,
		isDisabled,
		isReadOnly,
		readOnly: isReadOnly,
		placeholder,
		type: 'text',
		onFocus: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			if (focusProps.onFocus) focusProps.onFocus(event)
		},
		onBlur: (event: FocusEvent<HTMLInputElement | HTMLTextAreaElement>) => {
			if (focusProps.onBlur) focusProps.onBlur(event)
			return inputProps.onBlur(event)
		},
	} as const

	const ref = useMergedRefs<HTMLInputElement | HTMLTextAreaElement>(inputProps.ref, inputRef)

	return (
		<FieldWrapper isFullWidth>
			<ColumnLabel>
				<InputContainer
					{...hoverProps}
					hasError={hasError}
					isDisabled={isDisabled}
					isHovered={isHovered}
					isFocusVisible={isFocusVisible}
					isFocus={isFocused}
					isReadOnly={isReadOnly}
					onClick={(event) => {
						if (event.defaultPrevented) return undefined
						inputRef.current?.focus()
					}}
				>
					<IconWrapper>
						<IconSearch size={16} />
					</IconWrapper>
					<InputComponent {...defaultProps} ref={ref} readOnly={isReadOnly} />
				</InputContainer>
			</ColumnLabel>
		</FieldWrapper>
	)
}
